import { useState } from "react";
import React from "react";
import SearchIcon from "@mui/icons-material/Search";
import { Alert, Box, Button, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import type { GridColumnVisibilityModel } from "@mui/x-data-grid";
import useAsyncAbortableRequest from "hooks/useAsyncAbortableRequest";
import type { FilterValue } from "hooks/useFilters";
import useFilters from "hooks/useFilters";
import usePagination from "hooks/usePagination";
import { LoadablePage } from "shared/LoadablePage";
import { OrderStatus, SalesMotion } from "client/api-v2";
import { PageContent } from "components/PageContent/PageContent";
import PageHeading from "components/PageHeading";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { CreateQuoteDialog } from "areas/orders/components/CreateQuoteDialog";
import { OrderList, toggleableColumnFields } from "areas/orders/components/OrderList";
import { DisplayPopover } from "areas/orders/components/list/DisplayPopover";
import type { QuotesListColumnVisibility } from "areas/orders/components/list/DisplayPopover";
import {
  FilterPopover,
  allFilterValue,
  archivedFilterValues,
  quotesListFilterKeys,
  reviewFilterValues,
  salesRepFilterValues,
} from "areas/orders/components/list/FilterPopover";
import type { QuotesListFilters } from "areas/orders/components/list/FilterPopover";
import { useOpenNewQuote } from "areas/orders/hooks/useOpenNewQuote";
import { orderSearchHint } from "areas/orders/orderConstants";
import { getOrders, listOrderSalesReps } from "areas/orders/services/orderService";

const searchFilterKey = "search";

const searchTermMaxLength = 200;

const searchMissHint = "Check the term, or try another.";

function isOrderStatus(value: string): value is OrderStatus {
  return Object.values(OrderStatus).some((status) => status === value);
}

function isSalesMotion(value: string): value is SalesMotion {
  return Object.values(SalesMotion).some((salesMotion) => salesMotion === value);
}

function readListFilters(filters: { [key: string]: FilterValue }): QuotesListFilters {
  const read = (key: keyof QuotesListFilters): string => {
    const value = filters[key];
    return typeof value === "string" ? value : allFilterValue;
  };

  return {
    status: read("status"),
    fulfilment: read("fulfilment"),
    review: read("review"),
    motion: read("motion"),
    salesRep: read("salesRep"),
    existingSubscription: read("existingSubscription"),
    quoteSubscription: read("quoteSubscription"),
    archived: read("archived"),
  };
}

function OrderSearchForm({ initialTerm, onSearch }: { initialTerm: string; onSearch: (term: string) => void }) {
  const [term, setTerm] = useState(initialTerm);

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSearch(term.trim());
      }}
      sx={{ width: 420 }}
    >
      <TextField
        size="small"
        fullWidth
        placeholder={orderSearchHint}
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        slotProps={{
          htmlInput: { "aria-label": orderSearchHint, maxLength: searchTermMaxLength },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <IconButton type="submit" size="small" aria-label="Search">
                  <SearchIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
    </Box>
  );
}

export function OrdersPage() {
  const { page, setPage, pageSize, setPageSize } = usePagination();
  const { filters, setFilter, resetFilter } = useFilters({
    paramKeys: [...quotesListFilterKeys, searchFilterKey],
  });
  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>({});
  const [isCreatingQuote, setIsCreatingQuote] = useState(false);

  const openCreatedQuote = useOpenNewQuote();
  const openNewQuote = (odNumber: string) => {
    setIsCreatingQuote(false);
    openCreatedQuote(odNumber);
  };

  const handleFilterChange = (key: string, value: FilterValue) => {
    setPage(0);
    setFilter(key, value);
  };

  const handleResetFilters = () => {
    setPage(0);
    resetFilter();
  };

  const { data: salesReps = [] } = useAsyncAbortableRequest(listOrderSalesReps);

  const listFilters = readListFilters(filters);
  const activeSearch = typeof filters.search === "string" ? filters.search.slice(0, searchTermMaxLength) : undefined;

  const selectedStatus = isOrderStatus(listFilters.status) ? listFilters.status : undefined;
  const selectedMotion = isSalesMotion(listFilters.motion) ? listFilters.motion : undefined;
  const isSelfServeSelected = listFilters.salesRep === salesRepFilterValues.selfServe;
  const repFilterIsAWholeGroup = listFilters.salesRep === salesRepFilterValues.all || isSelfServeSelected;
  const selectedRep = repFilterIsAWholeGroup ? undefined : listFilters.salesRep;

  const {
    data = { items: [], totalItems: 0 },
    loading,
    error,
  } = useAsyncAbortableRequest(getOrders, {
    status: selectedStatus,
    salesMotion: selectedMotion,
    salesRep: selectedRep,
    selfServeOnly: isSelfServeSelected ? true : undefined,
    reviewRequired:
      listFilters.review === reviewFilterValues.needsReview
        ? true
        : listFilters.review === reviewFilterValues.noReviewRequired
        ? false
        : undefined,
    search: activeSearch,
    includeArchived: listFilters.archived === archivedFilterValues.included ? true : undefined,
    page: page + 1,
    pageSize,
  });

  const searchMatchedNothing = !loading && !error && !!activeSearch && data.items.length === 0;

  const visibility: QuotesListColumnVisibility = {
    fulfilment: columnVisibility[toggleableColumnFields.fulfilment] !== false,
    term: columnVisibility[toggleableColumnFields.term] !== false,
    lastUpdated: columnVisibility[toggleableColumnFields.lastUpdated] !== false,
  };

  const handleVisibilityChange = (key: keyof QuotesListColumnVisibility, isVisible: boolean) =>
    setColumnVisibility((model) => ({ ...model, [toggleableColumnFields[key]]: isVisible }));

  return (
    <>
      <PageHeading
        headingText="Orders"
        actionItems={[
          {
            policy: RbacPolicies.Orders.ReadWrite,
            component: (
              <Button variant="contained" onClick={() => setIsCreatingQuote(true)}>
                Create Quote
              </Button>
            ),
          },
        ]}
      />
      <PageContent>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap", mb: 2 }}>
          <OrderSearchForm initialTerm={activeSearch ?? ""} onSearch={(term) => handleFilterChange("search", term)} />
          <Box sx={{ flex: 1 }} />
          <FilterPopover
            filters={listFilters}
            salesReps={salesReps}
            onFilterChange={(key, value) => handleFilterChange(key, value === allFilterValue ? undefined : value)}
            onResetFilters={handleResetFilters}
          />
          <DisplayPopover visibility={visibility} onVisibilityChange={handleVisibilityChange} />
          <Typography variant="body2" color="text.secondary">
            {data.totalItems === 1 ? "1 order" : `${data.totalItems} orders`}
          </Typography>
        </Stack>
        <CreateQuoteDialog open={isCreatingQuote} onClose={() => setIsCreatingQuote(false)} onCreated={openNewQuote} />
        <LoadablePage error={error}>
          {searchMatchedNothing && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No orders matched &quot;{activeSearch}&quot;. {searchMissHint}
            </Alert>
          )}
          <OrderList
            orders={data.items}
            totalCount={data.totalItems}
            loading={loading}
            page={page}
            pageSize={pageSize}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </LoadablePage>
      </PageContent>
    </>
  );
}
